// Pure minimap draw functions over a minimal 2D-context interface.
//
//   drawStatic  - biome fill (cached on an offscreen canvas by the component)
//   drawDynamic - territory fill + borders, explored wash, cities, units, markers, hover/focus
//
// Fog tiles reveal nothing: no biome, no owner, no city, no unit, no marker. Same-style shapes
// are batched into one path per layer so a repaint is a handful of fill/stroke calls.

import { playerColor } from '../lib/playerColors.js';
import { FOG_COLOR, biomeColor } from './biomeColors.js';
import { clipPolygonToBounds, polygonArea } from './cameraViewBox.js';
import { type PreparedBoard, tileAt } from './minimapBoard.js';
import { CORNER_COS, CORNER_SIN, type Point, SQRT3, tileKey } from './minimapGeometry.js';
import { drawCity, drawMarkers, drawUnit } from './minimapPaintIcons.js';
import type { MinimapLayers } from './minimapPrefs.js';
import type { Ctx2D, DynamicPaintOptions, PaintView, TileRef } from './paintTypes.js';
import type { MinimapPing, MinimapTile } from './types.js';

const EXPLORED_WASH = 'rgba(8, 12, 18, 0.5)';
const TERRITORY_ALPHA = 0.3;
const HALO = 'rgba(0, 0, 0, 0.6)';
/** Overdraw (px) so neighbouring fills leave no anti-aliasing seams. */
const SEAM = 0.4;

/** Relief shade per terrain level (level x 0.012 alpha; level 12 = 0.14). */
const RELIEF_ALPHA_PER_LEVEL = 0.012;
const RIVER_COLOR = 'rgba(90, 150, 180, 0.95)';

/**
 * Translates a minimap tile's `riverEdges` mask (sim `AXIAL_DIRS` bit order:
 * E, NE, NW, W, SW, SE) into this painter's EDGE_NEIGHBORS order
 * (E, SE, SW, W, NW, NE).
 */
export const SIM_BIT_TO_UI_EDGE = [0, 5, 4, 3, 2, 1] as const;

/** A ping ring's lifetime; it expands from a tile to ~2 tiles and fades out. */
export const PING_MS = 1500;

const PING_COLORS: Record<MinimapPing['severity'], string> = {
  info: 'rgba(255,255,255,0.95)',
  minor: 'rgba(250,204,21,1)',
  major: 'rgba(248,113,113,1)',
};

/** The halo-then-colour passes the ping rings and the ownership borders use. */
const PASSES = [0, 1] as const;

/**
 * Expanding, fading rings for active pings. `nowMs` (performance.now()) comes from the
 * caller so the painter stays pure; with `animate` false (reduced motion or the
 * particle-effects setting off) each ping is a static full-size ring instead.
 */
export function drawPings(
  ctx: Ctx2D,
  view: PaintView,
  pings: readonly MinimapPing[],
  nowMs: number,
  animate: boolean,
): void {
  for (const ping of pings) {
    const age = Math.min(Math.max((nowMs - ping.startedAt) / PING_MS, 0), 1);
    if (!animate) {
      drawPingRing(ctx, view, ping, 1, 0.8);
      continue;
    }
    if (age >= 1) continue;
    drawPingRing(ctx, view, ping, age, 0.95 * (1 - age));
  }
}

function drawPingRing(
  ctx: Ctx2D,
  view: PaintView,
  ping: MinimapPing,
  age: number,
  alpha: number,
): void {
  const cx = tileCenterX(view, ping.q, ping.r);
  const cy = tileCenterY(view, ping.r);
  if (offscreen(view, cx, cy)) return;
  const radius = view.scale * (0.9 + 2.1 * age);
  ctx.globalAlpha = alpha;
  for (const pass of PASSES) {
    ctx.strokeStyle = pass === 0 ? HALO : PING_COLORS[ping.severity];
    ctx.lineWidth = pass === 0 ? 3.5 : 2;
    ctx.beginPath();
    ctx.arc(cx, cy, radius + (pass === 0 ? 1 : 0), 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

export function tileCenterX(view: PaintView, q: number, r: number): number {
  return SQRT3 * (q + r / 2) * view.scale + view.panX;
}
export function tileCenterY(view: PaintView, r: number): number {
  return 1.5 * r * view.scale + view.panY;
}

/** Is a hex centred here (circumradius s) wholly outside the canvas? */
function offscreen(view: PaintView, cx: number, cy: number): boolean {
  const m = view.scale + 2;
  return cx < -m || cy < -m || cx > view.width + m || cy > view.height + m;
}

/** Append a hex outline to the current path (no beginPath). */
function addHex(ctx: Ctx2D, cx: number, cy: number, size: number): void {
  for (let i = 0; i < 6; i++) {
    const x = cx + size * (CORNER_COS[i] ?? 0);
    const y = cy + size * (CORNER_SIN[i] ?? 0);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Add a tile to the bucket for `key`, creating the bucket on first use. */
function bucket<K>(map: Map<K, MinimapTile[]>, key: K, tile: MinimapTile): void {
  const list = map.get(key);
  if (list) list.push(tile);
  else map.set(key, [tile]);
}

/**
 * How many hexes share one path. A path with a few dozen subpaths still takes
 * the canvas' fast route; a path with thousands falls back to the slow
 * scan-converter, which is why a bucket is filled in chunks rather than in one
 * giant path.
 */
const PATH_CHUNK = 48;

/** Fill the hexes of `tiles` in chunks of PATH_CHUNK, one path per chunk. */
function fillHexes(ctx: Ctx2D, view: PaintView, tiles: readonly MinimapTile[], size: number): void {
  for (let i = 0; i < tiles.length; i += PATH_CHUNK) {
    const end = Math.min(i + PATH_CHUNK, tiles.length);
    ctx.beginPath();
    for (let j = i; j < end; j++) {
      const t = tiles[j];
      if (!t) continue;
      const cx = tileCenterX(view, t.q, t.r);
      const cy = tileCenterY(view, t.r);
      if (offscreen(view, cx, cy)) continue;
      addHex(ctx, cx, cy, size);
    }
    ctx.fill();
  }
}

/** Opaque base layer: biome colour per tile (fog = neutral), plus the rivers and
 * relief shading (M4b) — everything terrain-derived that the cache can hold.
 *
 * The tiles are grouped by what decides their paint, so a whole biome (or a whole
 * terrain level, or all the rivers) is ONE path and ONE fill/stroke instead of one
 * per tile: the draw-call count then tracks the number of biomes and levels, not
 * the number of tiles. On a 600-tile board that is ~16 calls instead of 892. */
export function drawStatic(
  ctx: Ctx2D,
  board: PreparedBoard,
  view: PaintView,
  layers?: Pick<MinimapLayers, 'rivers' | 'relief'>,
): void {
  ctx.clearRect(0, 0, view.width, view.height);
  const size = view.scale + SEAM;
  const showRivers = layers?.rivers ?? true;
  const showRelief = layers?.relief ?? true;

  const byColor = new Map<string, MinimapTile[]>();
  const byLevel = new Map<number, MinimapTile[]>();
  const riverTiles: MinimapTile[] = [];
  for (const t of board.tiles) {
    const cx = tileCenterX(view, t.q, t.r);
    const cy = tileCenterY(view, t.r);
    if (offscreen(view, cx, cy)) continue;
    const color = t.visibility === 'fog' || t.biome === '' ? FOG_COLOR : biomeColor(t.biome);
    bucket(byColor, color, t);
    // Relief: a terrain-level dark wash (fog tiles carry level 0).
    const level = t.terrainLevel ?? 0;
    if (showRelief && level > 0) bucket(byLevel, level, t);
    // Rivers: one stroke along each masked shared edge (edge e = corner e -> e+1).
    if (showRivers && (t.riverEdges ?? 0) !== 0) riverTiles.push(t);
  }

  for (const [color, tiles] of byColor) {
    ctx.fillStyle = color;
    fillHexes(ctx, view, tiles, size);
  }

  for (const [level, tiles] of byLevel) {
    ctx.fillStyle = `rgba(8, 12, 18, ${Math.min(level * RELIEF_ALPHA_PER_LEVEL, 0.16)})`;
    fillHexes(ctx, view, tiles, size);
  }

  if (riverTiles.length > 0) {
    // The corner offsets are the same for every tile (only the centre moves), so
    // they are computed once and no per-tile array is allocated.
    const rs = view.scale * 0.97;
    ctx.strokeStyle = RIVER_COLOR;
    ctx.lineWidth = Math.max(1, view.scale * 0.18);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (const t of riverTiles) {
      const cx = tileCenterX(view, t.q, t.r);
      const cy = tileCenterY(view, t.r);
      const mask = t.riverEdges ?? 0;
      for (let uiEdge = 0; uiEdge < 6; uiEdge++) {
        const simBit = SIM_BIT_TO_UI_EDGE[uiEdge];
        if (simBit === undefined || (mask & (1 << simBit)) === 0) continue;
        const b = (uiEdge + 1) % 6;
        ctx.moveTo(cx + rs * (CORNER_COS[uiEdge] ?? 0), cy + rs * (CORNER_SIN[uiEdge] ?? 0));
        ctx.lineTo(cx + rs * (CORNER_COS[b] ?? 0), cy + rs * (CORNER_SIN[b] ?? 0));
      }
    }
    ctx.stroke();
  }
}

function ownerColor(o: DynamicPaintOptions, owner: number): string {
  // Same palette as the 3D board (playerColor by player id) unless the caller overrides it.
  return o.playerColors?.get(owner) ?? playerColor(owner);
}

function ringHex(ctx: Ctx2D, view: PaintView, ref: TileRef, style: string, width: number): void {
  const cx = tileCenterX(view, ref.q, ref.r);
  const cy = tileCenterY(view, ref.r);
  ctx.beginPath();
  addHex(ctx, cx, cy, view.scale * 0.94);
  ctx.strokeStyle = HALO;
  ctx.lineWidth = width + 1.5;
  ctx.stroke();
  ctx.strokeStyle = style;
  ctx.lineWidth = width;
  ctx.stroke();
}

/** Everything that changes with the game state (not cached). Call after drawing the static layer. */
export function drawDynamic(
  ctx: Ctx2D,
  board: PreparedBoard,
  view: PaintView,
  o: DynamicPaintOptions = {},
): void {
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.setLineDash([]);
  const s = view.scale;

  // 1. Territory fill: one path per owner, owner colour at low alpha.
  const showTerritory = o.layers?.territory ?? true;
  const showFog = o.layers?.fog ?? true;
  const showUnits = o.layers?.units ?? true;
  if (showTerritory) {
    ctx.globalAlpha = TERRITORY_ALPHA;
    for (const layer of board.owners) {
      ctx.fillStyle = ownerColor(o, layer.owner);
      fillHexes(ctx, view, layer.tiles, s + SEAM);
    }
    ctx.globalAlpha = 1;
  }

  // 2. Explored wash (dims remembered-but-not-visible tiles, territory included).
  if (showFog && board.explored.length > 0) {
    ctx.fillStyle = EXPLORED_WASH;
    fillHexes(ctx, view, board.explored, s + SEAM);
  }

  // 3. Ownership borders: only edges whose neighbour has a different owner (dark halo + colour).
  const w = Math.min(3, Math.max(1.5, s * 0.2));
  const inset = s * 0.97;
  for (const layer of showTerritory ? board.owners : []) {
    if (layer.borders.length === 0) continue;
    for (const pass of PASSES) {
      ctx.strokeStyle = pass === 0 ? HALO : ownerColor(o, layer.owner);
      ctx.lineWidth = pass === 0 ? w + 1.6 : w;
      for (let i = 0; i < layer.borders.length; i += PATH_CHUNK) {
        const end = Math.min(i + PATH_CHUNK, layer.borders.length);
        ctx.beginPath();
        for (let j = i; j < end; j++) {
          const border = layer.borders[j];
          if (!border) continue;
          const { tile: t, mask } = border;
          const cx = tileCenterX(view, t.q, t.r);
          const cy = tileCenterY(view, t.r);
          if (offscreen(view, cx, cy)) continue;
          for (let e = 0; e < 6; e++) {
            if ((mask & (1 << e)) === 0) continue;
            const b = (e + 1) % 6;
            ctx.moveTo(cx + inset * (CORNER_COS[e] ?? 0), cy + inset * (CORNER_SIN[e] ?? 0));
            ctx.lineTo(cx + inset * (CORNER_COS[b] ?? 0), cy + inset * (CORNER_SIN[b] ?? 0));
          }
        }
        ctx.stroke();
      }
    }
  }

  // 4. Cities, then units (never on fog; the list only holds non-fog tiles).
  for (const t of showUnits ? board.icons : []) {
    const cx = tileCenterX(view, t.q, t.r);
    const cy = tileCenterY(view, t.r);
    if (offscreen(view, cx, cy)) continue;
    const key = tileKey(t.q, t.r);
    ctx.globalAlpha = t.visibility === 'explored' ? 0.7 : 1;
    if (t.hasCity) {
      drawCity(ctx, view, cx, cy, o.details?.cities?.get(key), t.controlledBy, o.playerColors);
    }
    if (t.hasUnit) {
      drawUnit(ctx, view, cx, cy, o.details?.units?.get(key), t, o.playerColors);
    }
    ctx.globalAlpha = 1;
  }

  // 5. Marker overlays.
  if (o.markers && o.markers.length > 0) {
    drawMarkers(ctx, board, view, o.markers, o.phase ?? 0, o.reducedMotion ?? false);
  }

  // 5.4. Ping rings.
  if (o.pings && o.pings.length > 0) {
    drawPings(ctx, view, o.pings, o.nowMs ?? 0, o.animatePings ?? true);
  }

  // 5.5. Camera view box: the ground footprint of the 3D camera, in unit space,
  // clipped to the board — a tilted camera's footprint runs far past the map
  // edge, and a box drawn off the map reads as a rendering bug (cameraViewBox.ts).
  if (o.cameraPolygon && o.cameraPolygon.length >= 3) {
    const poly = clipPolygonToBounds(o.cameraPolygon, board.bounds);
    // Draw the box only when it discriminates. At the default zoom the camera
    // footprint covers most of the board, so the clipped polygon is a huge
    // outline over the fog that tells the player nothing and reads as stray
    // white lines crossing the minimap (owner's report, 2026-10-22).
    const boardArea =
      (board.bounds.maxX - board.bounds.minX) * (board.bounds.maxY - board.bounds.minY);
    const worthDrawing = polygonArea(poly) < 0.5 * boardArea;
    if (poly.length >= 3 && worthDrawing) {
      ctx.beginPath();
      poly.forEach((pt: Point, i: number) => {
        const x = pt.x * view.scale + view.panX;
        const y = pt.y * view.scale + view.panY;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      // A dark halo under the white line: the box has to read over light terrain
      // (tundra, desert) as well as over dark terrain, in either theme. Dashed,
      // so it reads as an indicator rather than as a border of the map.
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = HALO;
      ctx.lineWidth = 2.4;
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,255,255,0.75)';
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  // 6. Hover + keyboard focus (only on tiles that exist).
  if (o.hover && tileAt(board, o.hover.q, o.hover.r)) {
    ringHex(ctx, view, o.hover, 'rgba(255,255,255,0.95)', 2);
  }
  if (o.focus && tileAt(board, o.focus.q, o.focus.r)) {
    ringHex(ctx, view, o.focus, 'rgba(250,204,21,1)', 2.5);
  }
}
